import {
    createRouter as _createRouter,
    createWebHistory,
    type NavigationGuardNext,
    type RouteLocation,
    type RouteLocationNormalized,
    type RouteLocationNormalizedLoaded
} from "vue-router";

import { LoadingBar } from "view-ui-plus";
import { type Store } from "vuex";

import store from "@/store";
import type { RootState } from "@/store/types";
import { UiAction } from "@/store/modules/ui";
import { routes } from "@/router/routes";
import { AppRouteNames } from "@/router/constants";

const router = _createRouter({
    history: createWebHistory(),
    routes
});

interface KeepQuery {
    iss?: string;
    error_description?: string;
    code?: string;
    [key: string]: unknown;
}

export function authN(route: RouteLocation) {
    const rootRoute = { name: AppRouteNames.HOME, meta: { theme: "default" } };

    const routeName = route.name;
    const ignoredRoutes = [
        AppRouteNames.LOGIN,
        AppRouteNames.USER_NOT_FOUND,
        AppRouteNames.NETWORK_ERROR,
        AppRouteNames.PREVIEW
    ];

    if (
        store.state.auth.isAuthorized &&
        (AppRouteNames.LOGIN === routeName || AppRouteNames.RESET_PASSWORD === routeName)
    ) {
        const { loginReturnRoute } = store.state.ui;

        if (loginReturnRoute) {
            store.commit(UiAction.SetLoginReturnRoute, null);

            return loginReturnRoute;
        }

        return { ...rootRoute, query: route.query };
    }

    if (
        !store.state.auth.isAuthorized &&
        routeName !== AppRouteNames.RESET_PASSWORD &&
        !ignoredRoutes.includes(routeName as (typeof ignoredRoutes)[number])
    ) {
        store.commit(UiAction.SetLoginReturnRoute, { ...route });

        return { name: AppRouteNames.LOGIN, query: route.query } as RouteLocation;
    }

    return null;
}

export function getLandingRoute(route: RouteLocation) {
    return authN(route) ?? ({ name: route.name } as RouteLocation);
}

/**
 *  We pass through if we detect the `code` query param as it is used for authN.
 *
 *  If we did not do this, the subsequent code would strip the query params out
 *  before the app had been initialized, meaning that SSO users would be unable
 *  to login.
 */
const keepQuery = (query: KeepQuery): boolean => Boolean(query.iss ?? query.error_description ?? query.code);

export const beforeEachGuard =
    (store: Store<RootState>) =>
    async (to: RouteLocationNormalized, from: RouteLocationNormalizedLoaded, next: NavigationGuardNext) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        LoadingBar.start();

        if (keepQuery(to.query)) {
            next();
            return;
        }

        const authNRedirectRoute = authN(to);

        if (authNRedirectRoute && typeof authNRedirectRoute !== "string" && authNRedirectRoute.name !== to.name) {
            if ("meta" in authNRedirectRoute && authNRedirectRoute.meta.theme) {
                await store.dispatch(UiAction.SetTheme, authNRedirectRoute.meta.theme);
            }

            next(authNRedirectRoute);
            return;
        }

        if (to.name === AppRouteNames.NETWORK_ERROR) {
            store.commit(UiAction.SetNetworkErrorReturnRoute, from);
        }

        next();
    };

export function initWithStore(store: Store<RootState>) {
    router.beforeEach(beforeEachGuard(store));
    router.afterEach(async to => {
        await store.dispatch(UiAction.SetTheme, to.meta.theme ?? store.state.ui.defaultTheme);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        LoadingBar.finish();
    });

    return router;
}
